/**
 * Responsibility: Model explicit item-provided actions and safe descriptor inference.
 * Scope: Item action semantics only; UI rendering and permission gating stay elsewhere.
 */
import { router } from '../ai/config'
import { extractJson } from '../ai/json-utils'
import { ITEM_ACTION_HINTS_SYSTEM, buildItemActionHintsPrompt } from '../ai/prompts'
import { parseItemActionHints } from './entities/forge-contract-artifact'
import type {
  ActionTargetRef,
  GameState,
  InventoryItem,
  ItemActionAvailability,
  ItemActionDescriptor,
  ItemActionExecution,
  ItemActionTargetHint,
} from "@anyquest/shared-core"

export type ItemActionContext = {
  location: 'inventory' | 'room'
  equipped?: boolean
  gs?: GameState | null
}

export type ItemActionHints = {
  readable: boolean
  presentable: boolean
  selfUse: boolean
  targetUse: boolean
  lightSource: boolean
}

export type ResolvedItemAction = ItemActionDescriptor & {
  input: string
  explicitTargets?: ActionTargetRef[]
}

const DEFAULT_ITEM_ACTION_HINTS: ItemActionHints = {
  readable: false,
  presentable: false,
  selfUse: false,
  targetUse: false,
  lightSource: false,
}

const itemActionHintCache = new Map<string, ItemActionHints>()
const itemActionHintPending = new Map<string, Promise<ItemActionHints>>()
let itemActionHintResolverForTest: ((itemName: string, item: Partial<InventoryItem> | null | undefined, context: ItemActionContext) => Promise<ItemActionHints>) | null = null

function toRefId(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function buildTarget(kind: ActionTargetRef['kind'], label: string, id?: string): ActionTargetRef {
  const trimmed = String(label ?? '').trim() || kind
  return {
    kind,
    id: String(id ?? (toRefId(trimmed) || kind)),
    label: trimmed,
  }
}

function normalizeDescriptor(raw: ItemActionDescriptor): ItemActionDescriptor | null {
  const id = String(raw?.id ?? '').trim()
  const label = String(raw?.label ?? '').trim()
  const inputTemplate = String(raw?.inputTemplate ?? '').trim()
  if (!id || !label || !inputTemplate) return null
  return {
    id,
    label,
    inputTemplate,
    mode: raw.mode === 'test' ? 'test' : 'do',
    category: raw.category ?? 'interact',
    when: raw.when === 'inventory' || raw.when === 'room' ? raw.when : 'any',
    targetHint: raw.targetHint === 'self' || raw.targetHint === 'active_npc' ? raw.targetHint : 'none',
    execution: raw.execution === 'item_forge' ? 'item_forge' : 'shared',
    tags: Array.isArray(raw.tags) ? raw.tags.map(tag => String(tag).trim()).filter(Boolean) : [],
  }
}

export function normalizeItemActionDescriptors(raw: ItemActionDescriptor[] | undefined | null): ItemActionDescriptor[] {
  const descriptors = Array.isArray(raw) ? raw : []
  const seen = new Set<string>()
  const next: ItemActionDescriptor[] = []
  for (const entry of descriptors) {
    const normalized = normalizeDescriptor(entry)
    if (!normalized) continue
    const key = normalized.id.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    next.push(normalized)
  }
  return next
}

function makeDescriptor(
  id: string,
  label: string,
  inputTemplate: string,
  mode: ItemActionDescriptor['mode'],
  category: ItemActionDescriptor['category'],
  when: ItemActionAvailability = 'any',
  targetHint: ItemActionTargetHint = 'none',
  execution: ItemActionExecution = 'shared',
  tags: string[] = [],
): ItemActionDescriptor {
  return {
    id,
    label,
    inputTemplate,
    mode,
    category,
    when,
    targetHint,
    execution,
    tags,
  }
}

function buildDescriptorSet(hints: ItemActionHints, context: ItemActionContext): ItemActionDescriptor[] {
  const descriptors: ItemActionDescriptor[] = []

  if (hints.readable) {
    descriptors.push(makeDescriptor('read', 'READ', 'Read {{item}}', 'test', 'examine', 'any', 'none', 'shared', ['item_read']))
  }

  if (hints.presentable && context.location === 'inventory') {
    descriptors.push(makeDescriptor('present', 'PRESENT', 'Present {{item}}', 'test', 'interact', 'inventory', 'none', 'shared', ['item_present']))
  }

  if (hints.selfUse && context.location === 'inventory') {
    descriptors.push(makeDescriptor('use_on_self', 'USE ON SELF', 'Use {{item}} on {{target}}', 'do', 'use', 'inventory', 'self', 'shared', ['item_use_self']))
  }

  if (hints.targetUse && context.location === 'inventory') {
    descriptors.push(makeDescriptor('use_on_target', 'USE ON TARGET', 'Use {{item}} on {{target}}', 'do', 'use', 'inventory', 'active_npc', 'shared', ['item_use_target']))
  }

  if (hints.lightSource) {
    descriptors.push(makeDescriptor('light', 'LIGHT', 'Light {{item}}', 'do', 'use', 'any', 'none', 'shared', ['item_light']))
  }

  return descriptors
}

function resolveDescriptorTarget(gs: GameState | null | undefined, hint: ItemActionTargetHint): ActionTargetRef[] {
  if (!gs || hint === 'none') return []
  if (hint === 'self') {
    const label = String(gs.player?.identity ?? gs.player?.name ?? 'Player').trim() || 'Player'
    return [buildTarget('player', label, 'player')]
  }
  if (hint === 'active_npc') {
    const activeTarget = String(gs.combat?.activeTargetName ?? '').trim()
    if (activeTarget) return [buildTarget('npc', activeTarget)]
    if ((gs.currentRoom?.npcs ?? []).length === 1) {
      return [buildTarget('npc', gs.currentRoom.npcs[0].name)]
    }
  }
  return []
}

function renderInput(template: string, itemName: string, targets: ActionTargetRef[]): string {
  const firstTarget = targets[0]?.label ?? ''
  return String(template)
    .replace(/\{\{item\}\}/g, itemName)
    .replace(/\{\{target\}\}/g, firstTarget)
    .trim()
}

function matchesContext(descriptor: ItemActionDescriptor, context: ItemActionContext): boolean {
  return descriptor.when === 'any' || descriptor.when === context.location
}

export function supportsSharedItemActionExecution(descriptor: Pick<ItemActionDescriptor, 'execution'> | null | undefined): boolean {
  return (descriptor?.execution ?? 'shared') === 'shared'
}

function itemHintCacheKey(itemName: string, item: Partial<InventoryItem> | null | undefined): string {
  const name = String(itemName ?? '').trim().toLowerCase()
  const desc = String(item?.desc ?? '').trim().toLowerCase()
  return `${name}::${desc}`.slice(0, 384)
}

async function inferItemActionHintsWithAI(
  itemName: string,
  item: Partial<InventoryItem> | null | undefined,
  context: ItemActionContext,
): Promise<ItemActionHints> {
  try {
    const result = await router.route<ItemActionHints | string>({
      jobType: 'logic_forge',
      systemPrompt: ITEM_ACTION_HINTS_SYSTEM,
      userPrompt: buildItemActionHintsPrompt(itemName, {
        desc: item?.desc,
        location: context.location,
        equipped: context.equipped,
        worldSeed: context.gs?.world?.seed,
      }),
    })
    const parsed = parseItemActionHints(typeof result === 'string' ? extractJson(result) : result)
    return parsed ?? DEFAULT_ITEM_ACTION_HINTS
  } catch (error) {
    console.warn('[ItemActionModel] Item affordance classification failed:', error)
    return DEFAULT_ITEM_ACTION_HINTS
  }
}

async function inferItemActionHints(
  itemName: string,
  item: Partial<InventoryItem> | null | undefined,
  context: ItemActionContext,
): Promise<ItemActionHints> {
  const cacheKey = itemHintCacheKey(itemName, item)
  const cached = itemActionHintCache.get(cacheKey)
  if (cached) return cached
  const pending = itemActionHintPending.get(cacheKey)
  if (pending) return pending
  const resolver = itemActionHintResolverForTest ?? inferItemActionHintsWithAI
  const promise = resolver(itemName, item, context)
    .then(result => {
      const normalized = {
        readable: result?.readable === true,
        presentable: result?.presentable === true,
        selfUse: result?.selfUse === true,
        targetUse: result?.targetUse === true,
        lightSource: result?.lightSource === true,
      }
      itemActionHintCache.set(cacheKey, normalized)
      return normalized
    })
    .finally(() => {
      itemActionHintPending.delete(cacheKey)
    })
  itemActionHintPending.set(cacheKey, promise)
  return promise
}

export function clearItemActionHintCache(): void {
  itemActionHintCache.clear()
  itemActionHintPending.clear()
}

export function setItemActionHintResolverForTest(
  resolver: ((itemName: string, item: Partial<InventoryItem> | null | undefined, context: ItemActionContext) => Promise<ItemActionHints>) | null,
): void {
  itemActionHintResolverForTest = resolver
}

export async function resolveItemActionDescriptors(
  itemName: string,
  item: Partial<InventoryItem> | null | undefined,
  context: ItemActionContext,
): Promise<ResolvedItemAction[]> {
  const explicit = normalizeItemActionDescriptors(item?.actions)
  const inferred = explicit.length
    ? []
    : buildDescriptorSet(await inferItemActionHints(itemName, item, context), context)
  const descriptors = explicit.length ? explicit : inferred
  const seen = new Set<string>()
  const next: ResolvedItemAction[] = []

  for (const descriptor of descriptors) {
    if (!matchesContext(descriptor, context)) continue
    const key = descriptor.id.toLowerCase()
    if (seen.has(key)) continue
    const explicitTargets = resolveDescriptorTarget(context.gs, descriptor.targetHint ?? 'none')
    if ((descriptor.targetHint === 'active_npc' || descriptor.targetHint === 'self') && !explicitTargets.length) continue
    seen.add(key)
    next.push({
      ...descriptor,
      input: renderInput(descriptor.inputTemplate, itemName, explicitTargets),
      ...(explicitTargets.length ? { explicitTargets } : {}),
    })
  }

  return next
}

export async function resolvePreferredCombatItemAction(
  itemName: string,
  item: Partial<InventoryItem> | null | undefined,
  context: ItemActionContext,
): Promise<ResolvedItemAction | null> {
  const actions = await resolveItemActionDescriptors(itemName, item, context)
  const supported = actions.filter(action => supportsSharedItemActionExecution(action))
  if (!supported.length) return null

  const prioritized = supported
    .filter(action => action.mode === 'do')
    .sort((a, b) => {
      const score = (entry: ResolvedItemAction): number => {
        if (entry.targetHint === 'active_npc') return 40
        if (entry.targetHint === 'self') return 30
        if (entry.category === 'use') return 20
        if (entry.category === 'attack') return 15
        return 10
      }
      return score(b) - score(a)
    })

  return prioritized[0] ?? null
}
